import { useState } from 'react';
import { motion } from 'framer-motion';
import { SEO } from '@/components/SEO';
import { Search } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { FAQ_DATA } from '@/lib/index';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

type CategoryFilter = 'toutes' | 'parents' | 'avocats' | 'processus';

const CATEGORY_LABELS: Record<CategoryFilter, string> = {
  toutes: 'Toutes',
  parents: 'Parents',
  avocats: 'Avocats',
  processus: 'Processus',
};

export default function FAQ() {
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('toutes');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFAQs = FAQ_DATA.filter((faq) => {
    const matchesCategory = selectedCategory === 'toutes' || faq.categorie === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.reponse.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Layout>
      <SEO
        title="Foire aux questions — Expertise familiale et médiation"
        description="Réponses à vos questions sur l'expertise psychosociale, la médiation familiale, le coaching coparental et le rétablissement du lien parent-enfant en Outaouais."
        keywords="FAQ médiation familiale Québec, questions expertise psychosociale, coûts médiation familiale, durée évaluation psychosociale, processus expertise garde"
        canonical="/faq"
      />
      <div className="min-h-screen bg-background">
        <motion.section
          className="relative py-24 bg-gradient-to-b from-muted/30 to-background"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={springPresets.gentle}
        >
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              variants={fadeInUp}
              initial="initial"
              animate="animate"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Questions fréquentes
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Trouvez des réponses claires et rassurantes à vos questions sur nos services,
                nos processus et notre approche professionnelle.
              </p>

              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Rechercher une question..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 text-base"
                />
              </div>
            </motion.div>
          </div>
        </motion.section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-4xl mx-auto"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              <motion.div
                className="flex flex-wrap gap-3 justify-center mb-12"
                variants={staggerItem}
              >
                {(Object.keys(CATEGORY_LABELS) as CategoryFilter[]).map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory(category)}
                    className="transition-all duration-200"
                  >
                    {CATEGORY_LABELS[category]}
                  </Button>
                ))}
              </motion.div>

              {filteredFAQs.length === 0 ? (
                <motion.div
                  className="text-center py-12"
                  variants={staggerItem}
                >
                  <p className="text-muted-foreground text-lg">
                    Aucune question ne correspond à votre recherche.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('toutes');
                    }}
                    className="mt-4"
                  >
                    Réinitialiser les filtres
                  </Button>
                </motion.div>
              ) : (
                <motion.div variants={staggerItem}>
                  <Accordion type="single" collapsible className="space-y-4">
                    {filteredFAQs.map((faq) => (
                      <AccordionItem
                        key={faq.id}
                        value={faq.id}
                        className="bg-card border border-border rounded-lg px-6 shadow-sm hover:shadow-md transition-shadow duration-200"
                      >
                        <AccordionTrigger className="text-left hover:no-underline py-6">
                          <span className="text-base font-semibold text-foreground pr-4">
                            {faq.question}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground pb-6 pt-2 leading-relaxed">
                          {faq.reponse}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>

        <motion.section
          className="py-16 bg-accent/10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.gentle}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Vous ne trouvez pas la réponse à votre question?
              </h2>
              <p className="text-muted-foreground mb-8">
                Notre équipe est disponible pour répondre à toutes vos questions et vous
                accompagner dans votre démarche. N'hésitez pas à nous contacter.
              </p>
              <Button size="lg" asChild>
                <a href="#/contact">Nous contacter</a>
              </Button>
            </div>
          </div>
        </motion.section>
      </div>
    </Layout>
  );
}
